import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@fragments';
import { IconLink, IconPhoto } from '@tabler/icons-react';
import { formatDelay, withUsername } from '../../../utils/format';

const Block = ({ title, children }) => <section className="studio-automation-summary__block">
  <Text.caption contrast={6}>{title}</Text.caption>
  {children}
</section>;

// `{{username}}` reads as a person here; the editor keeps the token.
const Message = ({ label, text, button, links, empty, person }) => <div className="studio-automation-summary__message">
  <Text.caption contrast={6}>{label}</Text.caption>
  <Text.span contrast={text ? 8 : 5}>{withUsername(text, person) || empty}</Text.span>
  {button && <span className="studio-automation-keyword">{button}</span>}
  {links?.map((link, index) => <Text.caption key={index} contrast={7} left={<IconLink />} maxLines={1}>{`${link.title} · ${link.url}`}</Text.caption>)}
</div>;

// The automation read back as a sentence: the same four parts as the editor, without the fields.
export default function Summary({ studio: s, automation, media }) {
  const { t } = useTranslation('studioAutomations');
  const { trigger, comment_reply: reply, dm } = automation;
  const isStories = trigger.channel === 'stories';
  const shared = { empty: t('previewEmptyMessage'), person: t('summaryPerson') };
  return <div className="studio-automation-summary">
    <Block title={t(`stepTitle_trigger_${trigger.channel}`)}>
      {trigger.channel !== 'direct_messages' && <Text.span contrast={8}>{t(trigger.media_scope === 'all' ? (isStories ? 'scopeAllStories' : 'scopeAll') : trigger.media_scope === 'next' ? (isStories ? 'scopeNextStory' : 'scopeNext') : isStories ? 'scopeSelectedStories' : 'scopeSelected')}</Text.span>}
      {trigger.channel === 'direct_messages' && <Text.span contrast={8}>{t('triggerDirectHint')}</Text.span>}
      {media.length > 0 && <div className="studio-automation-summary__media">
        {media.map(item => (item.preview?.url ? <img key={item.id} src={item.preview.url} alt="" /> : <span key={item.id}><IconPhoto /></span>))}
      </div>}
    </Block>
    <Block title={t(`stepTitle_keywords_${trigger.channel}`)}>
      {trigger.match === 'any' ? <Text.span contrast={8}>{t('matchAny')}</Text.span>
        : <div className="studio-automation-summary__words">{trigger.keywords.map(word => <span key={word} className="studio-automation-keyword">{word}</span>)}</div>}
      {reply.enabled && reply.templates.length > 0 && <Message {...shared} label={t('replyTitle')} text={reply.templates.join(' · ')} />}
    </Block>
    <Block title={t(`stepTitle_messages_${trigger.channel}`)}>
      {dm.opening.enabled && <Message {...shared} label={t('openingTitle')} text={dm.opening.text} button={dm.opening.button_label} />}
      {dm.follow_gate.enabled && <Message {...shared} label={t('followShort')} text={dm.follow_gate.text} button={dm.follow_gate.button_label} />}
      {dm.email_capture.enabled && <Message {...shared} label={t('emailShort')} text={dm.email_capture.text} />}
      {!dm.opening.enabled && !dm.follow_gate.enabled && !dm.email_capture.enabled && <Text.span contrast={6}>{t('summaryNoSequence')}</Text.span>}
    </Block>
    <Block title={t(`stepTitle_delivery_${trigger.channel}`)}>
      <Message {...shared} label={t('deliveryText')} text={dm.delivery.text} links={dm.delivery.buttons} />
      {dm.reminder.enabled && <Message {...shared} label={t('reminderSummary', { delay: formatDelay(dm.reminder.delay_minutes, s.locale) })} text={dm.reminder.message.text} links={dm.reminder.message.buttons} />}
    </Block>
  </div>;
}
